import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, CircularProgress, Box, TextField, InputAdornment,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Collapse, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';
const fmt = (n) => n != null ? `€${parseFloat(n).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '—';

const statusColor = (s) => ({ pending: 'warning', accepted: 'success', declined: 'error', countered: 'info' }[s] || 'default');
const statusLabel = (s) => ({ pending: 'In attesa', accepted: 'Accettata', declined: 'Rifiutata', countered: 'Controproposta' }[s] || s);

function ProposalRow({ p, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [counterPrice, setCounterPrice] = useState('');
  const [notes, setNotes] = useState(p.admin_notes || '');
  const [saving, setSaving] = useState(false);

  const update = async (status, extra = {}) => {
    setSaving(true);
    await onUpdate(p.id, { status, admin_notes: notes, ...extra });
    setSaving(false);
    setDialog(null);
  };

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell><IconButton size="small" onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></TableCell>
        <TableCell><Typography variant="body2">{new Date(p.created_at).toLocaleDateString('it-IT')}</Typography></TableCell>
        <TableCell sx={{ maxWidth: 220 }}><Typography variant="body2" noWrap title={p.item_title}>{p.item_title}</Typography></TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold">{p.name || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{p.email}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.disabled" sx={{ textDecoration: 'line-through' }}>{fmt(p.original_price)}</Typography>
          <Typography variant="body1" fontWeight="bold" color="primary.main">{fmt(p.proposed_price)}</Typography>
          {p.original_price > 0 && (
            <Typography variant="caption" color="success.main">
              −{((1 - p.proposed_price / p.original_price) * 100).toFixed(0)}%
            </Typography>
          )}
        </TableCell>
        <TableCell><Chip label={statusLabel(p.status)} color={statusColor(p.status)} size="small" /></TableCell>
        <TableCell>
          {p.status === 'pending' && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" variant="contained" color="success" onClick={() => update('accepted')}>Accetta</Button>
              <Button size="small" variant="outlined" color="info" onClick={() => setDialog('counter')}>Controproposta</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => update('declined')}>Rifiuta</Button>
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, bgcolor: 'grey.50' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2 }}>
                <Box><Typography variant="caption" color="text.secondary">Telefono</Typography><Typography variant="body2">{p.phone || '—'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Messaggio</Typography><Typography variant="body2">{p.message || '—'}</Typography></Box>
                {p.counter_price && <Box><Typography variant="caption" color="text.secondary">Controproposta</Typography><Typography variant="body2" fontWeight="bold">{fmt(p.counter_price)}</Typography></Box>}
                {p.admin_notes && <Box><Typography variant="caption" color="text.secondary">Note admin</Typography><Typography variant="body2">{p.admin_notes}</Typography></Box>}
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Chip label={`Scrivi a ${p.email}`} size="small" component="a"
                  href={`mailto:${p.email}?subject=La tua offerta: ${p.item_title}`} clickable variant="outlined" />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <Dialog open={dialog === 'counter'} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Controproposta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Offerta ricevuta: <strong>{fmt(p.proposed_price)}</strong></Typography>
          <TextField fullWidth label="Il tuo prezzo (€)" type="number" value={counterPrice}
            onChange={e => setCounterPrice(e.target.value)} size="small" sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} />
          <TextField fullWidth label="Note per il cliente" value={notes} onChange={e => setNotes(e.target.value)} size="small" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Annulla</Button>
          <Button variant="contained" disabled={!counterPrice || saving}
            onClick={() => update('countered', { counter_price: parseFloat(counterPrice) })}>
            {saving ? <CircularProgress size={16} /> : 'Invia controproposta'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function AdminProposalsPage() {
  const { authHeader } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/proposals/admin`, { headers: authHeader() });
      setProposals(data.proposals || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUpdate = async (id, body) => {
    await axios.patch(`${API}/proposals/admin/${id}`, body, { headers: authHeader() });
    await load();
  };

  const filtered = proposals.filter(p =>
    !search || p.item_title?.toLowerCase().includes(search.toLowerCase()) ||
    p.email?.toLowerCase().includes(search.toLowerCase()) ||
    p.name?.toLowerCase().includes(search.toLowerCase())
  );

  const pending = proposals.filter(p => p.status === 'pending').length;

  if (loading) return <Container sx={{ pt: 6, textAlign: 'center' }}><CircularProgress /></Container>;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Proposte d'acquisto</Typography>
        {pending > 0 && <Chip label={`${pending} in attesa`} color="warning" />}
      </Box>

      <TextField size="small" placeholder="Cerca per articolo, email, nome..." value={search}
        onChange={e => setSearch(e.target.value)} sx={{ mb: 3, width: 320 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />

      <Paper>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell width={40} />
              <TableCell>Data</TableCell>
              <TableCell>Articolo</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Offerta</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>Nessuna proposta</TableCell></TableRow>}
            {filtered.map(p => <ProposalRow key={p.id} p={p} onUpdate={handleUpdate} />)}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
}
